// src/components/NotificationBell.js
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, doc, updateDoc, getDoc, collectionGroup } from 'firebase/firestore';
import { FaBell, FaBullseye, FaExclamationTriangle, FaClipboardCheck, FaMedal } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../styles/NotificationBell.css';

const SESSION_STORAGE_KEY = 'acknowledgedNotificationIds';

const NotificationBell = ({ currentUserInfo }) => {
  const [feedbackNotifications, setFeedbackNotifications] = useState([]);
  const [pdiNotifications, setPdiNotifications] = useState([]);
  const [evaluationNotifications, setEvaluationNotifications] = useState([]);
  const [resultNotifications, setResultNotifications] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [isBadgeVisible, setIsBadgeVisible] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Listener para Feedbacks, Solicitações e Alertas de Humor
  useEffect(() => {
    if (!currentUserInfo?.uid || !currentUserInfo?.empresaId) return;
    const feedbacksRef = collection(db, 'Empresas', currentUserInfo.empresaId, 'feedbacks');
    const q = query(feedbacksRef, where('destinatarioId', '==', currentUserInfo.uid), where('lido', '==', false));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allUnreadItems = snapshot.docs.map(doc => {
        const data = doc.data();
        let type = 'feedback';
        if (data.tipo === 'solicitacao') type = 'solicitacao';
        if (data.tipo === 'alerta_humor') type = 'alerta_humor';
        return { id: doc.id, ...data, notificationType: type };
      });
      setFeedbackNotifications(allUnreadItems);
    });
    return () => unsubscribe();
  }, [currentUserInfo]);

  // Listener para Metas do PDI
  useEffect(() => {
    if (!currentUserInfo?.uid) return;
    const checkPdiMetas = async () => {
      const userPrivateDocRef = doc(db, 'usersprivado', currentUserInfo.uid);
      try {
        const docSnap = await getDoc(userPrivateDocRef);
        if (docSnap.exists() && docSnap.data().pdi && docSnap.data().pdi.metas) {
          const metas = docSnap.data().pdi.metas;
          const newPdiNotifications = [];
          const today = new Date(); today.setHours(0, 0, 0, 0);
          metas.forEach(meta => {
            if (meta.status === 'Concluído' || !meta.prazo) return;
            let prazoDate;
            if (meta.prazo.seconds) {
              prazoDate = new Date(meta.prazo.seconds * 1000);
            } else if (typeof meta.prazo === 'string') {
              prazoDate = new Date(meta.prazo + 'T00:00:00');
            } else { return; }
            prazoDate.setHours(0, 0, 0, 0);
            const daysDiff = Math.ceil((prazoDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
            if (daysDiff < 0) {
              newPdiNotifications.push({ id: `pdi-${meta.id}-atrasada`, texto: `Sua meta "${meta.descricao.substring(0, 30)}..." está atrasada!`, notificationType: 'pdi_atrasada', data: meta.prazo });
            } else if (daysDiff <= 3 && daysDiff >=0) {
              newPdiNotifications.push({ id: `pdi-${meta.id}-proxima`, texto: `Atenção: A meta "${meta.descricao.substring(0, 30)}..." vence em ${daysDiff+1} dia(s).`, notificationType: 'pdi_proxima', data: meta.prazo });
            }
          });
          setPdiNotifications(newPdiNotifications);
        }
      } catch (error) { console.error("Erro ao buscar metas do PDI:", error); }
    };
    checkPdiMetas();
    const intervalId = setInterval(checkPdiMetas, 3600000); 
    return () => clearInterval(intervalId);
  }, [currentUserInfo?.uid]);

  // Listener para Tarefas de Avaliação
  useEffect(() => {
    if (!currentUserInfo?.uid) return;
    const responsesRef = collectionGroup(db, 'responses');
    const q = query(responsesRef, where('evaluatorUserId', '==', currentUserInfo.uid), where('status', '==', 'pending'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newEvaluationTasks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        notificationType: 'avaliacao_desempenho',
        data: new Date()
      }));
      setEvaluationNotifications(newEvaluationTasks);
    }, (error) => console.error("Erro ao buscar tarefas de avaliação:", error));
    return () => unsubscribe();
  }, [currentUserInfo?.uid]);

  // Listener para Resultados de Avaliação Entregues (com Templates)
  useEffect(() => {
    if (!currentUserInfo?.uid || !currentUserInfo.empresaId) return;
    
    const participantsRef = collectionGroup(db, 'participants');
    const q = query(participantsRef, 
      where('userId', '==', currentUserInfo.uid),
      where('empresaId', '==', currentUserInfo.empresaId), // <-- CORREÇÃO APLICADA AQUI
      where('status', '==', 'delivered'),
      where('notificationRead', '==', false)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const templateDocRef = doc(db, 'Empresas', currentUserInfo.empresaId, 'notificationTemplates', 'resultDelivered');
      const templateSnap = await getDoc(templateDocRef);
      const defaultText = `Seus resultados da avaliação "{{cycleName}}" foram liberados!`;
      let templateText = defaultText;

      if (templateSnap.exists() && templateSnap.data().body) {
        templateText = templateSnap.data().body.replace(/<[^>]*>?/gm, ' ');
      }
      
      const newNotifications = await Promise.all(snapshot.docs.map(async (pDoc) => {
        const cycleRef = pDoc.ref.parent.parent;
        const cycleSnap = await getDoc(cycleRef);
        const cycleName = cycleSnap.exists() ? cycleSnap.data().name : 'um ciclo de avaliação';
        const userName = pDoc.data().userName || currentUserInfo.name;

        const finalText = templateText
          .replace(/{{userName}}/g, userName)
          .replace(/{{cycleName}}/g, cycleName);

        return {
          id: pDoc.id,
          path: pDoc.ref.path,
          notificationType: 'resultado_avaliacao',
          texto: finalText,
          data: pDoc.data().resultsReleasedAt || new Date()
        };
      }));
      setResultNotifications(newNotifications);
    }, (error) => console.error("Erro ao buscar notificações de resultados:", error));

    return () => unsubscribe();
  }, [currentUserInfo]);

  // Gerencia o badge de notificação
  useEffect(() => {
    const allNotifications = [...feedbackNotifications, ...pdiNotifications, ...evaluationNotifications, ...resultNotifications];
    if (allNotifications.length > 0) {
      const acknowledgedIds = new Set(JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY)) || []);
      const hasNewUnseenItems = allNotifications.some(item => !acknowledgedIds.has(item.id));
      if (hasNewUnseenItems) { setIsBadgeVisible(true); }
    } else { setIsBadgeVisible(false); }
  }, [feedbackNotifications, pdiNotifications, evaluationNotifications, resultNotifications]);

  // Lógica para fechar o painel ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) { setIsOpen(false); }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleBellToggle = () => {
    if (!isOpen) {
      const allCurrentIds = [...feedbackNotifications, ...pdiNotifications, ...evaluationNotifications, ...resultNotifications].map(n => n.id);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(allCurrentIds));
      setIsBadgeVisible(false);
    }
    setIsOpen(prev => !prev);
  };

  const handleNotificationClick = async (notification) => {
    setIsOpen(false);
    if (['feedback', 'solicitacao', 'alerta_humor'].includes(notification.notificationType)) {
      const docRef = doc(db, 'Empresas', currentUserInfo.empresaId, 'feedbacks', notification.id);
      await updateDoc(docRef, { lido: true });
      navigate(notification.notificationType === 'alerta_humor' ? '/minha-equipe' : '/dashboard');
    } else if (notification.notificationType.startsWith('pdi')) {
      navigate('/dashboard');
    } else if (notification.notificationType === 'avaliacao_desempenho') {
      navigate('/avaliacoes/minhas-tarefas');
    } else if (notification.notificationType === 'resultado_avaliacao') {
      const participantDocRef = doc(db, notification.path);
      await updateDoc(participantDocRef, { notificationRead: true });
      navigate('/meus-resultados');
    }
  };

  const allNotifications = [...feedbackNotifications, ...pdiNotifications, ...evaluationNotifications, ...resultNotifications].sort((a, b) => {
      const dateA = a.dataEnvio || a.dataSolicitacao || a.data;
      const dateB = b.dataEnvio || b.dataSolicitacao || b.data;
      if (!dateA || !dateB) return 0;
      const timeA = dateA.seconds ? dateA.toMillis() : new Date(dateA).getTime();
      const timeB = dateB.seconds ? dateB.toMillis() : new Date(dateB).getTime();
      return timeB - timeA;
  });

  const humorMap = { 'neutro': 'Neutro', 'triste': 'Triste', 'muito-triste': 'Muito Triste' };

  return (
    <div className="notification-bell-wrapper" ref={dropdownRef}>
      <button onClick={handleBellToggle} className="notification-bell-icon" aria-label="Notificações">
        <FaBell />
        {isBadgeVisible && allNotifications.length > 0 && <span className="notification-badge">{allNotifications.length}</span>}
      </button>
      {isOpen && (
        <div className="notification-panel">
          <div className="notification-header"><h4>Notificações</h4></div>
          <ul className="notification-list">
            {allNotifications.length > 0 ? (
              allNotifications.map(notif => (
                <li key={notif.id} className="notification-item" onClick={() => handleNotificationClick(notif)}>
                  {notif.notificationType === 'alerta_humor' && <p><FaExclamationTriangle className="icon-alerta"/> <strong>{notif.autorNome}</strong> registou o humor: <strong>{humorMap[notif.humor] || notif.humor}</strong>.</p>}
                  {notif.notificationType === 'feedback' && <p><FaBell className="icon-feedback"/> Você recebeu um novo feedback de <strong>{notif.autorNome || 'um colega'}</strong>.</p>}
                  {notif.notificationType === 'solicitacao' && <p><FaBell className="icon-solicitacao"/> <strong>{notif.solicitanteNome || 'Um colega'}</strong> solicitou seu feedback.</p>}
                  {notif.notificationType === 'pdi_proxima' && <p><FaBullseye className="icon-pdi-proxima"/> {notif.texto}</p>}
                  {notif.notificationType === 'pdi_atrasada' && <p><FaBullseye className="icon-pdi-atrasada"/> {notif.texto}</p>}
                  {notif.notificationType === 'avaliacao_desempenho' && <p><FaClipboardCheck className="icon-avaliacao"/> Você tem uma nova avaliação pendente para responder.</p>}
                  {notif.notificationType === 'resultado_avaliacao' && <p><FaMedal className="icon-resultado"/> {notif.texto}</p>}
                  
                  <small>{new Date(((notif.dataEnvio || notif.dataSolicitacao || notif.data)?.seconds || 0) * 1000 || (notif.dataEnvio || notif.dataSolicitacao || notif.data)).toLocaleString('pt-BR')}</small>
                </li>
              ))
            ) : <li className="notification-item-empty">Nenhuma nova notificação.</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;